import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import {
  DP02,
  DP44,
  fetchColor,
  formatDate,
  formatDateTime,
  formatEpochDate,
  formatToDecimalPlaces,
  minFixedDecimal,
  toNumberOrNull,
} from "@/helpers/helperFunctions";
import { useDarkTheme } from "@/contexts/DarkTheme";
import axios from "axios";
import { API_URL } from "@/config";
import { Card, CardContent, CardHeader } from "../ui/card";

const barGraph = ({ t }: any) => {
  let [loading, setLoading] = useState(false);
  let [data, setData] = useState([]);
  const { darkTheme } = useDarkTheme();
  const colors = useMemo(() => {
    if (data?.length) {
      return fetchColor({
        positive: "var(--foreground-green)",
        negative: "var(--foreground-red)",
      });
    } else {
      return {
        positive: "hsl(var(--accent))",
        negative: "hsl(var(--accent))",
      };
    }
  }, [data]);

  let recentTrades = () => {
    const url = new URL("/marketplace_recent_trades", API_URL);
    axios
      .get(url.toString(), {})
      .then((data: any) => {
        if (data?.statusText == "OK") {
          // moment
          let finalArr = data.data.map((each: any, index) => ({
            date: formatEpochDate(each.trade_closed_at, "DD-MMM-YY"),
            returns: each.actual_result,
            ts: formatEpochDate(each.trade_closed_at, "DD-MMM-YY HH:mm"),
          }));
          setData(finalArr);
        } else {
          console.warn("err", data);
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    recentTrades();
    const intervalId = setInterval(() => {
      recentTrades();
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const CustomTooltip: any = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const val = toNumberOrNull(payload[0].value);
      return (
        <div className="flex flex-col gap-2 text-sm bg-background text-foreground border rounded shadow-sm p-2">
          <div className="flex gap-1">
            {/* {t("Trade Result")}: */}
            <span
              className={
                val == null
                  ? ""
                  : val < 0
                  ? "text-red-foreground"
                  : "text-green-foreground"
              }
            >
              ${minFixedDecimal(payload[0].value as number, DP02, "N/A")}
            </span>
          </div>
          <div className="">{payload[0]?.payload?.ts}</div>
        </div>
      );
    }

    return null;
  };
  return (
    <Card className="!grow h-full border-none">
      <CardHeader className="p-0 text-sm  tracking-tight flex flex-row items-center justify-between">
        <div className="text-sm ml-[2%] font-semibold">
          {t("Recent Trades")}
        </div>
        <div className="flex items-center justify-center relative !mt-0 mr-[2%] text-xs">
          <div className="flex items-center justify-center relative w-[12px] h-[12px] mr-2">
            <div className="absolute top-[2px] right-[0px] w-2 h-2 bg-[#316ACD] rounded-full"></div>
            <div className="absolute top-[2px] right-[0px] w-2 h-2 bg-[#316ACD] rounded-full opacity-75 animate-ping"></div>
          </div>
          <span className="text-[#2252CC]">realtime</span>
        </div>
      </CardHeader>
      <CardContent className="h-[200px] sm:h-[95%] p-0">
        <div className="flex w-full h-full">
          <ChartContainer
            config={{ returns: { label: "Monthly returns" } }}
            className="aspect-auto w-full h-full"
          >
            <BarChart data={data}>
              <ReferenceLine y={0} stroke="hsl(var(--foreground))" />
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date" // Corrected the dataKey here
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />

              <ChartTooltip content={CustomTooltip} />
              <Bar dataKey="returns" type="linear" isAnimationActive={false}>
                {data.length > 0 &&
                  data.map((entry: any, index: number) => {
                    const val = toNumberOrNull(entry.returns, 0);
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          val < 0
                            ? colors?.negative
                            : val > 0
                            ? colors?.positive
                            : ""
                        }
                      />
                    );
                  })}
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default barGraph;
